import React from 'react';
import { Target, Eye, Heart, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Authenticity',
      description: 'We believe in bringing genuine, unadulterated products directly from trusted sources to your home.'
    },
    {
      icon: Target,
      title: 'Quality First',
      description: 'Every product undergoes rigorous quality checks to ensure it meets our premium standards.'
    },
    {
      icon: Users,
      title: 'Trust & Transparency',
      description: 'Building lasting relationships through honest practices and transparent communication.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1686008674009-876c599f1fe9"
            alt="Indian Agricultural Tradition and Quality"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-5xl font-bold text-white mb-4">About Hind Harvest</h1>
            <p className="text-xl text-gray-200">
              Bridging tradition and quality to bring India's finest FMCG products to the world.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Hind Harvest was born from a simple yet powerful vision: to organize and elevate 
                the distribution of premium FMCG products from India's vast unorganized sector. 
                We saw an opportunity to connect authentic, high-quality products with customers 
                who value tradition, purity, and excellence.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Starting with saffron - often called "red gold" - we chose to begin with a product 
                that embodies luxury, tradition, and the rich agricultural heritage of India. Our 
                premium Kashmiri saffron represents everything we stand for: authenticity, quality, 
                and a commitment to preserving traditional practices.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                As we grow, we remain dedicated to our core mission of bringing premium user 
                experiences through carefully curated FMCG products that tell the story of India's 
                agricultural excellence.
              </p>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/6739372/pexels-photo-6739372.png"
                alt="Aerial view of organized farmland at sunset - Deep roots, Large scale, Reliable operations"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision */}
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="w-14 h-14 bg-saffron-100 rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-saffron-600" size={28} />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                To become India's most trusted distribution network for premium FMCG products, 
                creating a bridge between traditional producers and modern consumers.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We envision a future where authentic, high-quality products from India's unorganized 
                sector reach every home, bringing with them the richness of our cultural heritage and 
                agricultural excellence.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="w-14 h-14 bg-saffron-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-saffron-600" size={28} />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                To deliver an exceptional user experience by providing premium, authentic FMCG products 
                that meet the highest standards of quality and purity.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We are committed to supporting local farmers and producers, ensuring fair practices, 
                and building a sustainable supply chain that benefits everyone involved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Hind Harvest.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-cream-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm">
                  <value.icon className="text-saffron-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Looking Ahead</h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              While we've started with premium saffron, our journey has just begun. We're actively 
              working on expanding our portfolio to include more carefully selected FMCG products 
              from India's rich agricultural landscape.
            </p>
            <p className="text-xl text-gray-300 leading-relaxed">
              Our goal is to build a comprehensive distribution network that brings the best of 
              India's unorganized sector to customers worldwide, maintaining our commitment to 
              quality, authenticity, and exceptional user experience at every step.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;